import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh, Videocam, Mic } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DeviceSelectionState } from '../types';

interface DeviceSelectorProps {
  deviceState: DeviceSelectionState;
  onCameraChange: (deviceId: string) => void;
  onMicrophoneChange: (deviceId: string) => void;
  onRefresh: () => void;
  compact?: boolean;
  isLiveStreaming?: boolean;
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  deviceState,
  onCameraChange,
  onMicrophoneChange,
  onRefresh,
  compact = false,
  isLiveStreaming = false,
}) => {
  const { t } = useTranslation();

  const handleCameraChange = (event: any) => {
    const deviceId = event.target.value;
    console.log('DeviceSelector: Camera change event:', deviceId);
    console.log('DeviceSelector: Current device state:', deviceState);
    if (deviceId) {
      console.log('DeviceSelector: Calling onCameraChange with:', deviceId);
      onCameraChange(deviceId);
    }
  };

  const handleMicrophoneChange = (event: any) => {
    const deviceId = event.target.value;
    console.log('DeviceSelector: Microphone change event:', deviceId);
    if (deviceId) {
      console.log('DeviceSelector: Calling onMicrophoneChange with:', deviceId);
      onMicrophoneChange(deviceId);
    }
  };

  if (compact) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        {/* Camera Selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="camera-select-label">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Videocam fontSize="small" />
              {t('Camera')}
            </Box>
          </InputLabel>
          <Select
            labelId="camera-select-label"
            value={deviceState.selectedCameraId || ''}
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <Videocam fontSize="small" />
                {t('Camera')}
              </Box>
            }
            onChange={handleCameraChange}
            disabled={deviceState.isLoading}
            size="small"
          >
            {deviceState.cameras.map((camera) => (
              <MenuItem key={camera.deviceId} value={camera.deviceId}>
                {camera.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Microphone Selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="microphone-select-label">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Mic fontSize="small" />
              {t('Microphone')}
            </Box>
          </InputLabel>
          <Select
            labelId="microphone-select-label"
            value={deviceState.selectedMicrophoneId || ''}
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <Mic fontSize="small" />
                {t('Microphone')}
              </Box>
            }
            onChange={handleMicrophoneChange}
            disabled={deviceState.isLoading}
            size="small"
          >
            {deviceState.microphones.map((microphone) => (
              <MenuItem key={microphone.deviceId} value={microphone.deviceId}>
                {microphone.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Refresh Button */}
        <Tooltip title={t('Refresh devices')}>
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={deviceState.isLoading}
          >
            {deviceState.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Refresh fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">{t('Device Selection')}</Typography>
        <Tooltip title={t('Refresh devices')}>
          <IconButton
            onClick={onRefresh}
            disabled={deviceState.isLoading}
          >
            {deviceState.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Refresh />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {deviceState.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(deviceState.error)}
        </Alert>
      )}

      {isLiveStreaming && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can now switch devices during live streaming. The stream will continue with the new device.
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={2}>
        {/* Camera Selection */}
        <FormControl fullWidth>
          <InputLabel id="camera-select-label">
            <Box display="flex" alignItems="center" gap={1}>
              <Videocam />
              {t('Camera')}
            </Box>
          </InputLabel>
          <Select
            labelId="camera-select-label"
            value={deviceState.selectedCameraId || ''}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Videocam />
                {t('Camera')}
              </Box>
            }
            onChange={handleCameraChange}
            disabled={deviceState.isLoading}
          >
            {deviceState.cameras.map((camera) => (
              <MenuItem key={camera.deviceId} value={camera.deviceId}>
                {camera.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Microphone Selection */}
        <FormControl fullWidth>
          <InputLabel id="microphone-select-label">
            <Box display="flex" alignItems="center" gap={1}>
              <Mic />
              {t('Microphone')}
            </Box>
          </InputLabel>
          <Select
            labelId="microphone-select-label"
            value={deviceState.selectedMicrophoneId || ''}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Mic />
                {t('Microphone')}
              </Box>
            }
            onChange={handleMicrophoneChange}
            disabled={deviceState.isLoading}
          >
            {deviceState.microphones.map((microphone) => (
              <MenuItem key={microphone.deviceId} value={microphone.deviceId}>
                {microphone.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {deviceState.cameras.length === 0 && deviceState.microphones.length === 0 && !deviceState.isLoading && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('No media devices found. Please check your camera and microphone permissions.')}
        </Alert>
      )}
    </Box>
  );
};
